import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { GoogleMap, useLoadScript, MarkerF, Libraries } from '@react-google-maps/api';
import { Alert, CircularProgress } from '@mui/material';
import { useTheme } from '../../hooks/ThemeProvider';
import { useGeolocation, DEFAULT_LOCATION } from '../../hooks/GeolocationProvider';

const MAP_DEFAULT_ZOOM = 16;
const MAP_LIBRARIES: Libraries = ['places', 'geometry'];

export const MAP_STYLES_LIGHT: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cccccc' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#767676' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dddddd' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#ffffff' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e6f7ff' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
];

export const MAP_STYLES_DARK: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#444444' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#424242' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#ababab' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#1e3a54' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#b9b9b9' }] }
];

export const MAP_OPTIONS: google.maps.MapOptions = {
  clickableIcons: false,
  disableDefaultUI: true,
  fullscreenControl: false,
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: false,
};

export interface BaseMapProps {
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  showUserLocation?: boolean;
  className?: string;
  style?: React.CSSProperties;
  mapContainerStyle?: React.CSSProperties;
  onMapLoad?: (map: google.maps.Map) => void;
  onMapClick?: (event: google.maps.MapMouseEvent) => void;
  children?: React.ReactNode;
  // For direct Google Maps API access
  useDirectAPI?: boolean;
  mapContainerRef?: React.RefObject<HTMLDivElement>;
  // Override default options
  mapOptions?: Partial<google.maps.MapOptions>;
}

const BaseMap: React.FC<BaseMapProps> = ({
  center,
  zoom = MAP_DEFAULT_ZOOM,
  showUserLocation = true,
  className,
  style,
  mapContainerStyle,
  onMapLoad,
  onMapClick,
  children,
  useDirectAPI = false,
  mapContainerRef,
  mapOptions = {}
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: MAP_LIBRARIES
  });
  const { userGeolocation } = useGeolocation();
  const { isDarkMode } = useTheme();

  const mapRef = useRef<google.maps.Map | null>(null);

  // Create map options with the appropriate style based on theme
  const finalMapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    ...mapOptions,
    styles: isDarkMode ? MAP_STYLES_DARK : MAP_STYLES_LIGHT
  }), [isDarkMode, mapOptions]);

  const mapCenter = center || userGeolocation || DEFAULT_LOCATION;

  useEffect(() => {
    if (userGeolocation && mapRef.current) {
      mapRef.current.panTo(userGeolocation);
    }
  }, [userGeolocation]);

  // Update map styles when theme changes
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({
        styles: isDarkMode ? MAP_STYLES_DARK : MAP_STYLES_LIGHT
      });
    }
  }, [isDarkMode]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    // Only add event listeners if map and div exist
    if (map && map.getDiv()) {
      const mapDiv = map.getDiv();
      mapDiv.addEventListener('touchstart', () => { }, { passive: true });
      mapDiv.addEventListener('touchmove', () => { }, { passive: true });
    }
    onMapLoad?.(map);
  }, [onMapLoad]);

  if (loadError) {
    return <Alert severity="error">Error loading maps</Alert>;
  }

  if (!isLoaded) {
    return (<div className='loading-overlay'><CircularProgress /></div>);
  }

  // For direct API usage (InteractiveMapPlanner)
  if (useDirectAPI) {
    return (
      <div
        ref={mapContainerRef}
        className={className}
        style={style}
        data-testid="google-map-container"
      />
    );
  }

  // For react-google-maps-api usage (Map.tsx)
  return (
    <div
      data-testid="google-map-container"
      style={{ width: '100%', height: '100%', ...style }}
      className={className}
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', ...mapContainerStyle }}
        center={mapCenter}
        zoom={zoom}
        options={finalMapOptions}
        onLoad={handleMapLoad}
        onClick={onMapClick}
      >
        {showUserLocation && userGeolocation && (
          <MarkerF
            position={userGeolocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#00ffff',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            }}
            zIndex={1000}
          />
        )}
        {children}
      </GoogleMap>
    </div>
  );
};

export default BaseMap;
